// src/components/BankPosting/components/BankPostingSearchBox.jsx
// ============================================================================
// BLOCK BP.CMP.001 — Search box with quick-clear control
// ----------------------------------------------------------------------------
// PURPOSE
// - Provides the repeated Bank Posting search control used across panels
// - Keeps the orange wireframe-aligned label styling in one shared component
// - Adds one-click clear behavior so users can reset search state quickly
// - Supports optional placeholder/helper text hints for search syntax
//
// USER EXPERIENCE
// - Label sits above the input on the right
// - Clear icon only appears when a value is present
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import ClearIcon from '@mui/icons-material/Clear';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const selectableTextFieldSx = {
  '& .MuiInputBase-input': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    cursor: 'text',
  },
  '& .MuiInputBase-root': {
    cursor: 'text',
  },
};

// ============================================================================
// BLOCK BP.CMP.001.01 — Main search-box component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders label + input + conditional clear button
// - Keeps search reset logic local and reusable
// ============================================================================
export default function BankPostingSearchBox({ label, value, onChange, placeholder, helperText }) {
  const handleClear = React.useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <Box>
      {/* Right-aligned orange tag label used by wireframe */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#d97a2b',
            color: '#fff',
            px: 1.25,
            py: 0.375,
            borderRadius: 0.5,
            lineHeight: 1,
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            {label}
          </Typography>
        </Box>
      </Box>

      {/* Search input with clear icon only when value is populated */}
      <TextField
        size="small"
        fullWidth
        value={value}
        placeholder={placeholder}
        helperText={helperText}
        onChange={(e) => onChange(e.target.value)}
        sx={selectableTextFieldSx}
        inputProps={{ style: { paddingTop: 7, paddingBottom: 7, userSelect: 'text' } }}
        InputProps={{
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                onClick={handleClear}
                aria-label={`Clear ${label}`}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
}

BankPostingSearchBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
};

BankPostingSearchBox.defaultProps = {
  placeholder: '',
  helperText: '',
};
